/**
 * @brisa/ui · facelift/brand/SquiggleLogo
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #9 (logotipo-reduzido)
 * Figma origem: node 163:903 (logotipo-reduzido, asset SVG oficial)
 *
 * Logotipo facelift v1 — squiggle amarelo oficial fornecido pelo PL.
 * `currentColor` no fill → consumidor define cor via `text-accent-yellow`
 * Tailwind class ou via `style={{ color: 'hsl(var(--accent-yellow))' }}`.
 *
 * 3 sizes (sm/md/lg). Aspect ratio nativo do SVG = 39:38 (≈ 1.026:1).
 * Sem 'use client' — apresentacional puro.
 */
import * as React from 'react';

import { cn } from '@brisa/ui/lib/utils';

export type SquiggleLogoSize = 'sm' | 'md' | 'lg';

const SIZE_DIMENSIONS: Record<SquiggleLogoSize, { width: number; height: number }> = {
  sm: { width: 24, height: 23 },
  md: { width: 40, height: 39 },
  lg: { width: 64, height: 62 },
};

export interface SquiggleLogoProps extends React.SVGAttributes<SVGSVGElement> {
  size?: SquiggleLogoSize;
}

export function SquiggleLogo({
  size = 'md',
  className,
  'aria-label': ariaLabel,
  ...props
}: SquiggleLogoProps) {
  const { width, height } = SIZE_DIMENSIONS[size];
  const labelled = !!ariaLabel;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 39 38"
      fill="none"
      role={labelled ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={labelled ? undefined : true}
      focusable={false}
      className={cn('text-accent-yellow', className)}
      data-slot="squiggle-logo"
      {...props}
    >
      <path
        d="M21.4961 13.3506C22.1851 13.122 22.6507 13.0731 23.2578 13.1562H23.2588C23.4944 13.188 23.7517 13.2404 23.9736 13.2988C24.2057 13.3599 24.3662 13.4197 24.4297 13.4561L24.4326 13.458C24.6149 13.5602 25.0453 13.8778 25.3809 14.165V14.166C25.656 14.4008 25.7585 14.5169 25.8203 14.6641C25.8895 14.8288 25.9216 15.0626 25.9531 15.5723C25.9962 16.2604 25.9149 16.9514 25.7207 17.5635C25.5958 17.9564 25.3052 18.6442 25.001 19.2754L24.6973 19.875C24.3051 20.6081 23.433 21.9448 22.7646 22.8369C22.0976 23.7272 20.7925 25.2746 19.8721 26.2695C19.1767 27.0197 18.3204 27.9239 17.7002 28.5625L17.1729 29.0977C16.6075 29.6614 15.7981 30.5281 15.3721 31.0303C14.9522 31.5252 14.295 32.3134 13.9111 32.7852C13.6132 33.1514 13.0061 34.0355 12.3994 34.9746L11.8047 35.915C11.536 36.3483 11.2624 36.7959 11.0107 37.2119C6.89902 35.3136 3.55981 32.0273 1.59668 27.9521L1.73926 27.9609C2.56724 27.9964 3.47084 27.951 4.07227 27.833C4.67682 27.7139 5.4335 27.5261 5.75781 27.4102C6.07539 27.2965 6.86818 26.9485 7.51855 26.6387L7.86523 26.4609C8.65804 26.0263 9.36986 25.4245 10.5029 24.2734C11.4492 23.3103 13.0113 21.5395 13.9785 20.3301C14.9368 19.1318 16.252 17.5918 16.8965 16.9111V16.9102C17.538 16.229 18.6363 15.212 19.3252 14.6553C20.0849 14.0398 20.939 13.5332 21.4951 13.3506H21.4961ZM38.6631 20.1211C38.4557 26.7453 34.9457 32.5357 29.7236 35.9014C29.6728 35.8708 29.6241 35.8421 29.5791 35.8135C29.1051 35.512 28.5806 35.1067 28.3242 34.8594L28.2314 34.7637C28.0142 34.5151 27.5865 33.9469 27.2852 33.502C27.0216 33.1128 26.921 32.9363 26.8721 32.707C26.819 32.4576 26.8239 32.13 26.8564 31.4277V31.4268C26.906 30.35 27.0313 29.8087 27.376 29.0986L27.377 29.0977C27.6222 28.5904 28.3012 27.6768 28.8779 27.0928C29.4579 26.5055 30.4065 25.7111 30.9727 25.334C31.5606 24.9443 32.2676 24.4719 32.5439 24.2852C32.6753 24.1964 32.8045 24.1167 32.9033 24.0605C32.9529 24.0324 32.9911 24.012 33.0166 24C33.0249 23.9961 33.03 23.9931 33.0322 23.9922C33.043 23.9897 33.052 23.9906 33.0566 23.9893C33.076 23.9835 33.094 23.9761 33.1074 23.9707C33.1346 23.9598 33.1641 23.9465 33.1924 23.9326C33.2501 23.9042 33.3228 23.8655 33.4033 23.8203C33.5656 23.7294 33.7739 23.6047 33.9902 23.4697H33.9912C34.4116 23.2056 35.1217 22.7884 35.5674 22.5439C36.0374 22.2861 36.7883 21.7778 37.25 21.4043C37.6905 21.0492 38.3077 20.4871 38.6631 20.1211ZM33.0166 23.9971C33.009 23.9989 32.9841 24.0049 32.9502 24.0049C32.9831 24.0049 33.0113 23.9967 33.0312 23.9922C33.0286 23.9931 33.0234 23.9954 33.0166 23.9971ZM19.1748 0C23.362 4.88957e-05 27.2405 1.32015 30.418 3.56641C29.8156 3.63457 29.1709 3.74327 28.6826 3.86621C27.8642 4.07276 26.6949 4.4878 26.0693 4.79492C25.456 5.09613 24.4609 5.66411 23.8496 6.0625C23.2375 6.46146 22.1929 7.23169 21.5215 7.77637C20.8511 8.32017 19.2904 9.66606 18.0518 10.7666C16.8204 11.8607 15.0423 13.2784 14.1064 13.9121C13.1649 14.5496 11.7477 15.3714 10.9668 15.7354C10.1771 16.1034 9.05024 16.5307 8.47461 16.6826H8.47363C7.90296 16.8347 6.72105 16.9629 5.82812 16.9629C4.93867 16.9647 3.68669 16.8833 3.05957 16.7832H3.05859C2.43048 16.6846 1.32233 16.4035 0.611328 16.1611C0.431942 16.1 0.223356 16.0224 0 15.9355C1.6748 6.86807 9.62261 0 19.1748 0Z"
        fill="currentColor"
      />
    </svg>
  );
}
